import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import MagicLogin from "@/pages/MagicLogin";
import Jobs from "@/pages/Jobs";
import Customers from "@/pages/Customers";
import CustomerSavedMeasurements from "@/pages/CustomerSavedMeasurements";
import AdvertorialArticle from "@/pages/AdvertorialArticle";
import AdvertorialsAdmin from "@/pages/settings/AdvertorialsAdmin";
import FixEstimateTotals from "@/pages/settings/FixEstimateTotals";
import NearbyEstimates from "@/pages/NearbyEstimates";
import AIIntegration from "@/pages/settings/AIIntegration";
import Messages from "@/pages/Messages";
import AITextApprovals from "@/pages/AITextApprovals";
import Settings from "@/pages/Settings";
import Availability from "@/pages/Availability";
import TimeOff from "@/pages/TimeOff";
import ZipRouting from "@/pages/ZipRouting";
import TechManagement from "@/pages/TechManagement";
import Intake from "@/pages/Intake";
import InstantEstimate from "@/pages/InstantEstimate";
import ScreenDoorQuote from "@/pages/ScreenDoorQuote";
import SwingingDoorQuote from "@/pages/SwingingDoorQuote";
import SwingingDoorPackages from "@/pages/SwingingDoorPackages";
import MemorialDay from "@/pages/MemorialDay";
import Resources from "@/pages/Resources";
import ManageResources from "@/pages/ManageResources";
import Invoices from "@/pages/Invoices";
import Estimates from "@/pages/Estimates";
import JobUpdates from "@/pages/JobUpdates";
import TaxReport from "@/pages/TaxReport";
import ItemsPricing from "@/pages/settings/ItemsPricing";
import CampaignLinks from "@/pages/settings/CampaignLinks";
import CallLog from "@/pages/settings/CallLog";
import RosieAutoQuote from "@/pages/settings/RosieAutoQuote";
import ReviewTemplate from "@/pages/settings/ReviewTemplate";
import ReviewTracking from "@/pages/settings/ReviewTracking";
import TechAssistLog from "@/pages/settings/TechAssistLog";
import ReplyDraftsLog from "@/pages/settings/ReplyDraftsLog";
import TechPlaybook from "@/pages/settings/TechPlaybook";
import Notifications from "@/pages/settings/Notifications";
import QuickReplies from "@/pages/settings/QuickReplies";
import EmailDripReporting from "@/pages/settings/EmailDripReporting";
import GrowthBoard from "@/pages/GrowthBoard";
import LeadSources from "@/pages/settings/LeadSources";
import EmailDripTemplates from "@/pages/settings/EmailDripTemplates";
import EstimateFormSettings from "@/pages/settings/EstimateForm";
import FunnelAnalytics from "@/pages/settings/FunnelAnalytics";
import Promotions from "@/pages/settings/Promotions";
import MarketingPerformance from "@/pages/settings/MarketingPerformance";
import OpenEstimatesMap from "@/pages/settings/OpenEstimatesMap";
import RevenueBreakdown from "@/pages/settings/RevenueBreakdown";
import TechPerformance from "@/pages/settings/TechPerformance";
import ScreenDoorFunnelAnalytics from "@/pages/settings/ScreenDoorFunnelAnalytics";
import DriveTime from "@/pages/settings/DriveTime";
import ReviewRedirect from "@/pages/ReviewRedirect";
import ReceiptView from "@/pages/ReceiptView";
import InvoicePayView from "@/pages/InvoicePayView";
import FollowUpSequenceSettings from "@/pages/FollowUpSequenceSettings";
import Analytics from "@/pages/Analytics";
import { Toaster } from "@/components/ui/sonner";
import { setupAxiosInterceptor } from "@/utils/auth";
import { captureAttribution } from "@/utils/attribution";
import axios from "axios";
import "@/App.css";

captureAttribution();

// Setup axios to include session token from localStorage
setupAxiosInterceptor(axios);

function AppRouter() {
  const location = useLocation();
  
  // Check for session_id in hash or sessionStorage (set by index.js before redirects)
  // Must check BEFORE any route renders to prevent auth redirect loops
  const hashHasSession = location.hash?.includes('session_id=') || window.location.hash?.includes('session_id=');
  const pendingSession = sessionStorage.getItem('pending_session_id');
  
  if (hashHasSession || pendingSession) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/magic-login" element={<MagicLogin />} />
      <Route path="/intake" element={<Intake />} />
      <Route path="/instant-estimate" element={<InstantEstimate />} />
      <Route path="/screen-door-quote" element={<ScreenDoorQuote />} />
      <Route path="/swinging-door-quote" element={<SwingingDoorQuote />} />
      <Route path="/swinging-door-packages" element={<SwingingDoorPackages />} />
      <Route path="/receipt/:invoiceId" element={<ReceiptView />} />
      <Route path="/invoice/:invoiceId" element={<InvoicePayView />} />
      <Route path="/memorial" element={<MemorialDay />} />
      <Route path="/memorial-day-sale" element={<MemorialDay />} />
      <Route path="/jobs" element={<Jobs />} />
      <Route path="/calendar" element={<Jobs />} /> {/* Redirect old calendar to jobs */}
      <Route path="/customers" element={<Customers />} />
      <Route path="/customers/:customerId/measurements" element={<CustomerSavedMeasurements />} />
      <Route path="/learn/:slug" element={<AdvertorialArticle />} />
      <Route path="/settings/advertorials" element={<AdvertorialsAdmin />} />
      <Route path="/settings/fix-estimate-totals" element={<FixEstimateTotals />} />
      <Route path="/estimates/nearby" element={<NearbyEstimates />} />
      <Route path="/settings/ai-integration" element={<AIIntegration />} />
      <Route path="/messages" element={<Messages />} />
      <Route path="/ai-texts" element={<AITextApprovals />} />
      <Route path="/invoices" element={<Invoices />} />
      <Route path="/estimates" element={<Estimates />} />
      <Route path="/resources" element={<Resources />} />
      <Route path="/settings" element={<Settings />} />
      <Route path="/settings/growth-board" element={<GrowthBoard />} />
      <Route path="/settings/lead-sources" element={<LeadSources />} />
      <Route path="/settings/availability" element={<Availability />} />
      <Route path="/settings/time-off" element={<TimeOff />} />
      <Route path="/settings/zip-routing" element={<ZipRouting />} />
      <Route path="/settings/resources" element={<ManageResources />} />
      <Route path="/techs" element={<TechManagement />} />
      <Route path="/settings/marketing/job-updates" element={<JobUpdates />} />
      <Route path="/settings/reports/tax" element={<TaxReport />} />
      <Route path="/settings/items-pricing" element={<ItemsPricing />} />
      <Route path="/settings/campaign-links" element={<CampaignLinks />} />
      <Route path="/settings/call-log" element={<CallLog />} />
      <Route path="/settings/rosie-auto-quote" element={<RosieAutoQuote />} />
      <Route path="/settings/review-template" element={<ReviewTemplate />} />
      <Route path="/settings/review-tracking" element={<ReviewTracking />} />
      <Route path="/settings/tech-assist" element={<TechAssistLog />} />
      <Route path="/settings/reply-drafts" element={<ReplyDraftsLog />} />
      <Route path="/settings/tech-playbook" element={<TechPlaybook />} />
      <Route path="/settings/notifications" element={<Notifications />} />
      <Route path="/settings/quick-replies" element={<QuickReplies />} />
      <Route path="/settings/email-drip-reporting" element={<EmailDripReporting />} />
      <Route path="/settings/email-drip-templates" element={<EmailDripTemplates />} />
      <Route path="/settings/estimate-form" element={<EstimateFormSettings />} />
      <Route path="/settings/follow-up-sequence" element={<FollowUpSequenceSettings />} />
      <Route path="/settings/funnel-analytics" element={<FunnelAnalytics />} />
      <Route path="/settings/promotions" element={<Promotions />} />
      <Route path="/settings/marketing-performance" element={<MarketingPerformance />} />
      <Route path="/settings/revenue-breakdown" element={<RevenueBreakdown />} />
      <Route path="/settings/tech-performance" element={<TechPerformance />} />
      <Route path="/settings/screen-door-funnel" element={<ScreenDoorFunnelAnalytics />} />
      <Route path="/settings/drive-time" element={<DriveTime />} />
      <Route path="/settings/open-estimates-map" element={<OpenEstimatesMap />} />
      <Route path="/quote" element={<InstantEstimate />} />
      <Route path="/r/:token" element={<ReviewRedirect />} />
      <Route path="/analytics" element={<Analytics />} />
      <Route path="/" element={<Navigate to="/jobs" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-center" />
      </BrowserRouter>
    </div>
  );
}

export default App;